import { useNewRelic } from '@/composables/useNewRelic';
import type { CatalogData } from '../types/catalog-pdf.types';

type NewRelicClient = ReturnType<typeof useNewRelic>;

function recordMetric(...args: Parameters<NewRelicClient['addPageAction']>) {
  return useNewRelic().addPageAction(...args);
}

function log(...args: Parameters<NewRelicClient['log']>) {
  return useNewRelic().log(...args);
}

function noticeError(...args: Parameters<NewRelicClient['noticeError']>) {
  return useNewRelic().noticeError(...args);
}

function getHeapUsageMb(): number | null {
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number };
  };

  if (perf.memory) {
    return Math.round((perf.memory.usedJSHeapSize / 1024 / 1024) * 100) / 100;
  }

  return null;
}

function getCatalogCounts(catalogData: CatalogData) {
  const categoryCount = catalogData.categories.length;
  const productCount = catalogData.categories.reduce((sum, category) => sum + category.products.length, 0);
  const genderGroupCount = catalogData.categories.reduce(
    (sum, category) => sum + category.products.reduce((productSum, product) => productSum + product.genders.length, 0),
    0,
  );
  const colorwayCount = catalogData.categories.reduce(
    (sum, category) =>
      sum +
      category.products.reduce(
        (productSum, product) =>
          productSum + product.genders.reduce((genderSum, gender) => genderSum + gender.colorways.length, 0),
        0,
      ),
    0,
  );

  return {
    categoryCount,
    productCount,
    genderGroupCount,
    colorwayCount,
  };
}

export function recordCatalogPdfGenerationMetrics(params: {
  totalDurationMs: number;
  buildDurationMs: number;
  imagesLoadDurationMs?: number;
  downloadDurationMs?: number;
  fetchDurationMs?: number;
  fileSizeBytes?: number;
  accountId: string;
  season: string;
  catalogData: CatalogData;
  accountName?: string;
  legalEntity?: string;
}) {
  const memoryMb = getHeapUsageMb();
  const counts = getCatalogCounts(params.catalogData);
  const message = `Catalog PDF generated: accountId=${params.accountId}, season=${params.season}, categoryCount=${counts.categoryCount}, productCount=${counts.productCount}, colorwayCount=${counts.colorwayCount}, totalDurationMs=${params.totalDurationMs}`;
  const customAttributes = {
    feature: 'CatalogPdfGeneration',
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
    season: params.season,
    catalogName: params.catalogData.catalogName,
    totalDurationMs: params.totalDurationMs,
    buildDurationMs: params.buildDurationMs,
    ...(params.imagesLoadDurationMs !== undefined && { imagesLoadDurationMs: params.imagesLoadDurationMs }),
    ...(params.downloadDurationMs !== undefined && { downloadDurationMs: params.downloadDurationMs }),
    ...(params.fetchDurationMs !== undefined && { fetchDurationMs: params.fetchDurationMs }),
    ...(params.fileSizeBytes !== undefined && { fileSizeBytes: params.fileSizeBytes }),
    memoryMb,
    ...counts,
  };

  recordMetric('catalog_pdf_generation_duration_ms', {
    totalDurationMs: params.totalDurationMs,
    buildDurationMs: params.buildDurationMs,
    ...(params.imagesLoadDurationMs !== undefined && { imagesLoadDurationMs: params.imagesLoadDurationMs }),
    ...(params.downloadDurationMs !== undefined && { downloadDurationMs: params.downloadDurationMs }),
    ...(params.fetchDurationMs !== undefined && { fetchDurationMs: params.fetchDurationMs }),
    ...(params.fileSizeBytes !== undefined && { fileSizeBytes: params.fileSizeBytes }),
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
    season: params.season,
    catalogName: params.catalogData.catalogName,
    ...counts,
  });

  if (memoryMb !== null) {
    recordMetric('memory_usage_mb', {
      memoryMb,
      operation: 'catalog-pdf-generation',
      accountId: params.accountId,
      season: params.season,
    });
  }

  log(message, {
    level: 'INFO',
    customAttributes,
  });
}

export function recordCatalogPdfGenerationError(params: {
  error: Error;
  accountId: string;
  season: string;
  accountName?: string;
  legalEntity?: string;
  catalogName?: string;
  stage?: 'build' | 'download' | 'unknown';
}) {
  const baseAttrs = {
    feature: 'CatalogPdfGeneration',
    accountId: params.accountId,
    accountName: params.accountName,
    legalEntity: params.legalEntity,
    season: params.season,
    catalogName: params.catalogName,
    stage: params.stage ?? 'unknown',
  };
  const message = `Catalog PDF generation error: ${params.error.message}`;
  const customAttributes = {
    ...baseAttrs,
    errorName: params.error.name,
    errorStack: params.error.stack,
  };

  noticeError(params.error, baseAttrs);

  log(message, {
    level: 'ERROR',
    customAttributes,
  });
}
